import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fingerprint stored in IndexedDB — survives localStorage clears
async function getOrCreateFingerprint() {
  const DB_NAME = 'fg_db', STORE = 'fg', KEY = 'id';
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = (e) => e.target.result.createObjectStore(STORE);
      req.onsuccess = (e) => {
        const db = e.target.result;
        const store = db.transaction(STORE, 'readwrite').objectStore(STORE);
        const get = store.get(KEY);
        get.onsuccess = () => {
          if (get.result) { resolve(get.result); return; }
          const id = `${Math.random().toString(36).slice(2)}-${navigator.hardwareConcurrency||0}-${screen.width}x${screen.height}-${Intl.DateTimeFormat().resolvedOptions().timeZone}`;
          store.put(id, KEY);
          resolve(id);
        };
        get.onerror = () => resolve('fallback-' + Math.random().toString(36).slice(2));
      };
      req.onerror = () => resolve('fallback-' + Math.random().toString(36).slice(2));
    } catch { resolve('fallback-' + Math.random().toString(36).slice(2)); }
  });
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('ferdia-token'));
  const [loading, setLoading] = useState(true);
  const [fingerprintReady, setFingerprintReady] = useState(false);
  const [loginEnabled, setLoginEnabled] = useState(true);
  const [registerEnabled, setRegisterEnabled] = useState(true);

  // Initialize fingerprint and fetch auth config before any component fires requests
  useEffect(() => {
    getOrCreateFingerprint().then(fp => {
      axios.defaults.headers.common['x-fingerprint'] = fp;
      setFingerprintReady(true);
    });
    axios.get(`${API}/auth/config`).then(res => {
      setLoginEnabled(res.data.login_enabled);
      setRegisterEnabled(res.data.registration_enabled);
    }).catch(() => {
      // If config fetch fails, default to enabled so nothing breaks
    });
  }, []);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    const controller = new AbortController();
    const verify = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }, signal: controller.signal
        });
        setUser(response.data);
      } catch {
        if (!controller.signal.aborted) {
          localStorage.removeItem('ferdia-token');
          setToken(null);
          setUser(null);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    verify();
    return () => controller.abort();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('ferdia-token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  // Register no longer auto logs in — user must verify email first
  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    if (response.data.requires_verification) {
      return { requiresVerification: true };
    }
    const { access_token, user: userData } = response.data;
    localStorage.setItem('ferdia-token', access_token);
    setToken(access_token);
    setUser(userData);
    return { requiresVerification: false };
  };

  const deleteAccount = async () => {
    await axios.delete(`${API}/auth/delete-account`, { headers: { Authorization: `Bearer ${token}` } });
    logout();
  };

  const logout = () => {
    localStorage.removeItem('ferdia-token');
    setToken(null);
    setUser(null);
  };

  const getAuthHeaders = useCallback(() => token ? { Authorization: `Bearer ${token}` } : {}, [token]);

  return (
    <AuthContext.Provider value={{ user, token, loading, fingerprintReady, loginEnabled, registerEnabled, login, register, logout, deleteAccount, getAuthHeaders }}>
      {children}
    </AuthContext.Provider>
  );
};
